import {Button, MenuItem, Paper, TextField} from '@material-ui/core';
import AddressChangeRequestForm from 'Agent/Policies/components/AddressChangeRequest';
import DirectBillingChangeRequest from 'Agent/Policies/components/DirectBillingChangeRequest';
import PayrollAllotmentBillingChanges from 'Agent/Policies/components/PayrollAllotmentBillingChanges';
import {
  changeInsuredFirstName,
  changeInsuredLastName,
  changeInsuredMiddleName,
  changeMissingRequiredFields,
  changePolicyNumber,
  changeRequestType,
  clear,
  savePolicyServiceRequest,
  stepHandleNext,
  submitInitialRequest,
} from 'Agent/Policies/ducks/policyServiceRequest';
import {getNonEmptyStringErrors, NonEmptyString} from 'Core/models/EmptyString';
import {searchPoliciesByPolicyNumber} from 'Core/ducks/search';
import {validatePolicyNumberErrors} from 'Agent/Policies/ducks/policyValidation';
import {matchChangeRequest} from 'Agent/Policies/models/ServiceRequest/ChangeRequest';
import {
  ChangeType,
  InitialPolicyServiceRequest,
  isValidInitialPolicyServiceRequest,
  PolicyNumber,
  ValidInitialPolicyServiceRequest,
  ValidPolicyNumber,
} from 'Agent/Policies/models/ServiceRequest/InitialPolicyServiceRequest';
import {matchPolicyServiceRequest} from 'Agent/Policies/models/ServiceRequest/PolicyServiceRequest';
import {
  isValidPolicyServiceRequest,
  PolicyServiceRequestWithChangeRequest,
} from 'Agent/Policies/models/ServiceRequest/PolicyServiceRequestWithChangeRequest';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {compose} from 'redux';
import {ReducerState} from 'rootReducer';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {isPolicyNumberEIS} from 'Shared/utilities/regexUtility';
import {withEventValue} from 'Shared/utilities/WithEventValue';
import './PolicyServiceRequest.sass';
import Loading from 'Shared/components/Loading';

type IntialPolicyServiceRequestFormProps = {
  serviceRequest: InitialPolicyServiceRequest;
};

export const validateEISPolicyNumber = (policyNumber: string) => (
  additionalPolicyNumbers: ValidPolicyNumber[] | PolicyNumber[]
) =>
  isPolicyNumberEIS(policyNumber) ||
  additionalPolicyNumbers.some(policyNum => isPolicyNumberEIS(policyNum.value.value));

const InitialPolicyServiceRequestForm = (props: IntialPolicyServiceRequestFormProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {serviceRequest} = props;
  const match = useRouteMatch();
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);

  const {results} = useSelector((state: ReducerState) => state.search);

  const matchPolicyNumberErrorOrApi = (policyNumber: string) => {
    let output = validatePolicyNumberErrors(NonEmptyString.create(policyNumber));
    if (!output.value.length && (results.key.match('Empty') || !results.key.match('Loading'))) {
      dispatch(searchPoliciesByPolicyNumber(policyNumber, true));
    }
  };

  const matchResults = <TData, TReturn>(key: string) => {
    switch (key) {
      case 'Loaded':
        if (results['Data']?.length) return <div></div>;
        else return <div className="error_helper_text">Invalid Policy Number</div>;
      case 'Empty':
        return <div></div>;
      case 'Loading':
        return (
          <div>
            <Loading />
          </div>
        );
      case 'Conflict':
      case 'ServerError':
      case 'NotFound':
      case 'NoData':
      case 'NoContent':
      case 'Unauthorized':
      case 'NotStarted':
      default:
        return <div className="error_helper_text">Invalid Policy Number</div>;
    }
  };

  const disablePCRFiels = () =>
    validatePolicyNumberErrors(serviceRequest.policyNumber).value.length ||
    !(results.key === 'Loaded' && results['Data']?.length);

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: false}));
  }, []);

  useBreadcrumbs('Policy Service Request', match.url);

  return (
    <form className="policy-service-request__form">
      <h1>Change Policy Service Request</h1>
      <p>Please enter a valid Policy Number to complete the Policy Information section below.</p>

      <div className="policy-service-request__policy-numbers">
        <p>Policy Number</p>
        <ValidatableTextField
          label="Policy Number"
          className="service-request-form__field"
          errors={validatePolicyNumberErrors(serviceRequest.policyNumber)}
          onChange={e => {
            compose(dispatch, changePolicyNumber)(e);
            matchPolicyNumberErrorOrApi(e);
          }}
          inputProps={{
            minLength: 10,
            maxLength: 50,
          }}
          helperText={matchResults(results.key)}
          value={serviceRequest.policyNumber.value}
        />
      </div>
      <div className="service-request-form__section">
        <strong className="service-request-form__subheader">Policy Information</strong>
        <ValidatableTextField
          label="Insured First Name"
          disabled={disablePCRFiels() as boolean}
          className="service-request-form__field"
          errors={getNonEmptyStringErrors(serviceRequest.insuredFirstName)}
          onChange={compose(dispatch, changeInsuredFirstName)}
          value={serviceRequest.insuredFirstName.value}
        />
        <TextField
          label="Insured Middle Name"
          disabled={disablePCRFiels() as boolean}
          className="service-request-form__field"
          variant="outlined"
          onChange={withEventValue(compose(dispatch, changeInsuredMiddleName))}
          value={serviceRequest.insuredMiddleName.value}
        />
        <ValidatableTextField
          label="Insured Last Name"
          disabled={disablePCRFiels() as boolean}
          className="service-request-form__field"
          errors={getNonEmptyStringErrors(serviceRequest.insuredLastName)}
          onChange={compose(dispatch, changeInsuredLastName)}
          value={serviceRequest.insuredLastName.value}
        />
        <OutlinedSelect
          label="Service Request Change Type"
          disabled={disablePCRFiels() as boolean}
          className="service-request-form__request-change-type-select"
          fullWidth
          value={O.isSome(serviceRequest.requestChangeType) ? serviceRequest.requestChangeType.value : ''}
          onChange={e => {
            dispatch(changeRequestType(parseInt(e.target.value)));
          }}
        >
          <MenuItem value={ChangeType.AddressChange}>Address</MenuItem>
          <MenuItem value={ChangeType.DirectBillingChange}>Direct Billing</MenuItem>
          <MenuItem value={ChangeType.BillingScheduleChange}>Billing Schedule</MenuItem>
        </OutlinedSelect>
      </div>
      <div>
        <Button
          onClick={() => history.push(CoreRouteConstants.DASHBOARD)}
          className="service-request-form__cancel-button"
          variant="contained"
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={!isValidInitialPolicyServiceRequest(serviceRequest) || !results['Data']?.length}
          onClick={e => {
            if (isValidInitialPolicyServiceRequest(serviceRequest)) {
              dispatch(submitInitialRequest(serviceRequest));
            }
          }}
        >
          Next
        </Button>
      </div>
    </form>
  );
};

type PolicyServiceRequestHeaderProps = {
  validInitialServiceRequest: ValidInitialPolicyServiceRequest;
};

const PolicyServiceRequestHeader = (props: PolicyServiceRequestHeaderProps) => {
  return (
    <div className="policy-service-request__inital-request-header" data-dd-privacy="mask">
      <span>
        <strong>Policy Number:</strong> {props.validInitialServiceRequest.policyNumber.value}
      </span>
      <span>
        <strong>Insured Name:</strong>{' '}
        {`${props.validInitialServiceRequest.insuredLastName.value}, ${props.validInitialServiceRequest.insuredFirstName.value} ${props.validInitialServiceRequest.insuredMiddleName.value}`}
      </span>
      <span>
        {props.validInitialServiceRequest.additionalPolicyNumbers.length > 0 && (
          <>
            <strong>Additional Policies:</strong>{' '}
            {props.validInitialServiceRequest.additionalPolicyNumbers
              .map(policyNum => policyNum.value.value)
              .join(', ')}
          </>
        )}
      </span>
    </div>
  );
};

type PolicyServiceRequestFormWithChangeRequestProps = {
  serviceRequest: PolicyServiceRequestWithChangeRequest;
} & WithSelectedAgencyProps;

const PolicyServiceRequestFormWithChangeRequest = (props: PolicyServiceRequestFormWithChangeRequestProps) => {
  const {selectedAgency, serviceRequest} = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const {missingRequiredFields, unValidatedAddressAccepted} = useSelector(
    (state: ReducerState) => state.policyServiceRequest
  );
  const [missingFields, setMissingFields] = useState<String[]>([]);

  const getHandleNext = () => {
    if (serviceRequest.changeRequest.key === 'ValidChangeAddress') {
      setMissingFields(missingRequiredFields);

      return unValidatedAddressAccepted
        ? stepHandleNext(dispatch, changeMissingRequiredFields, missingRequiredFields, serviceRequest.changeRequest)
        : undefined;
    }
  };

  const validServiceRequestFlag = () => {
    if (serviceRequest.changeRequest.key === 'ValidChangeAddress' && !unValidatedAddressAccepted) {
      return !unValidatedAddressAccepted;
    } else {
      return !isValidPolicyServiceRequest(serviceRequest);
    }
  };

  return (
    <div className="service-request-form__change-request">
      {matchChangeRequest(serviceRequest.changeRequest, {
        AddressChangeRequest: address => <h3>Address Change Request</h3>,
        DirectBillingChangeRequest: directBilling => <h1>Direct Billing Change Request</h1>,
        PayrollAllotmentChangeRequest: payroll => <h1>Payroll Allotment Change Request</h1>,
      })}
      <PolicyServiceRequestHeader validInitialServiceRequest={serviceRequest.initialPolicyServiceRequestInformation} />
      {matchChangeRequest(serviceRequest.changeRequest, {
        AddressChangeRequest: address => <AddressChangeRequestForm addressChangeRequest={address} />,
        DirectBillingChangeRequest: directBilling => (
          <DirectBillingChangeRequest directBillingChangeRequest={directBilling} />
        ),
        PayrollAllotmentChangeRequest: payroll => (
          <PayrollAllotmentBillingChanges payrollAllotmentBillingChanges={payroll} />
        ),
      })}
      <div style={{padding: '32px 0 16px 0', textAlign: 'left'}}>
        <Button
          onClick={() => history.push(CoreRouteConstants.DASHBOARD)}
          className="service-request-form__cancel-button"
          variant="contained"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isValidPolicyServiceRequest(serviceRequest) ? validServiceRequestFlag() : true}
          onClick={e => {
            if (
              isValidPolicyServiceRequest(serviceRequest) &&
              serviceRequest.changeRequest.key == 'ValidChangeAddress'
            ) {
              getHandleNext();
              if (missingFields.length === 0) {
                dispatch(savePolicyServiceRequest(selectedAgency.encryptedIdentifier, serviceRequest));
              }
            } else {
              if (isValidPolicyServiceRequest(serviceRequest)) {
                dispatch(savePolicyServiceRequest(selectedAgency.encryptedIdentifier, serviceRequest));
              }
            }
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

const PolicyServiceRequest = (props: WithSelectedAgencyProps) => {
  const {serviceRequest} = useSelector((state: ReducerState) => state.policyServiceRequest);
  const {selectedAgency} = props;
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(clear());
    };
  }, []);

  return (
    <div className="policy-service-request">
      <Paper className="policy-service-request__form-wrapper">
        {matchPolicyServiceRequest(serviceRequest, {
          Initial: initial => <InitialPolicyServiceRequestForm serviceRequest={initial} />,
          WithChangeRequest: withChangeRequest => (
            <>
              <PolicyServiceRequestFormWithChangeRequest
                selectedAgency={selectedAgency}
                serviceRequest={withChangeRequest}
              />
            </>
          ),
        })}
      </Paper>
    </div>
  );
};

export default withSelectedAgency(PolicyServiceRequest);
